import { useEffect, useState } from 'react';
import { useSearch as useRouteSearch } from '@tanstack/react-router';
import { Search as SearchIcon, Sparkles } from 'lucide-react';
import { AssetGrid } from '@/components/AssetGrid';
import { EmptyState, ErrorState, CenteredSpinner } from '@/components/ui';
import { useFacets, useSearch } from '@/lib/queries';

/**
 * Meta-tag / AI search — queries the AI-generated fields (parity with the old
 * custom search modules): free text across all fields + brand and image-type
 * facet filters. Seeded from URL search params so the sidebar's brand/type
 * facet links land here pre-filtered.
 */
export function SearchPage() {
  const routeSearch = useRouteSearch({ strict: false }) as { q?: string; brand?: string; imageType?: string };
  const [q, setQ] = useState(routeSearch.q ?? '');
  const [brand, setBrand] = useState(routeSearch.brand ?? '');
  const [imageType, setImageType] = useState(routeSearch.imageType ?? '');
  const { data: facets } = useFacets();

  // Re-seed from the URL when a sidebar facet link changes the params.
  useEffect(() => {
    setQ(routeSearch.q ?? '');
    setBrand(routeSearch.brand ?? '');
    setImageType(routeSearch.imageType ?? '');
  }, [routeSearch.q, routeSearch.brand, routeSearch.imageType]);

  const hasCriteria = q.trim().length >= 2 || brand !== '' || imageType !== '';
  const search = useSearch(
    { q: q.trim() || undefined, brand: brand || undefined, imageType: imageType || undefined, perPage: 200 },
    hasCriteria,
  );

  return (
    <div className="flex h-full flex-col">
      <header className="space-y-3 border-b line surface-card px-5 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-brand-500" />
          <h1 className="text-base font-semibold text-strong">AI Search</h1>
        </div>
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            placeholder="Search by brand, product, description, hashtags…"
            className="w-full rounded-lg border line surface-card py-2 pl-9 pr-3 text-sm text-strong placeholder:text-faint focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <select value={brand} onChange={(e) => setBrand(e.target.value)} className="rounded border line surface-card px-2 py-1 text-xs text-strong">
            <option value="">All brands</option>
            {(facets?.brands ?? []).map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={imageType} onChange={(e) => setImageType(e.target.value)} className="rounded border line surface-card px-2 py-1 text-xs text-strong">
            <option value="">All image types</option>
            {(facets?.imageTypes ?? []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {search.data && <span className="ml-auto text-muted tabular-nums">{search.data.total} results</span>}
        </div>
      </header>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        {!hasCriteria ? (
          <EmptyState icon={<SearchIcon />} title="Enter a search term" description="At least two characters, or filter by brand/image type. Searches the AI tags." />
        ) : search.isPending ? (
          <CenteredSpinner />
        ) : search.error ? (
          <ErrorState error={search.error} onRetry={() => void search.refetch()} />
        ) : (search.data?.items.length ?? 0) === 0 ? (
          <EmptyState icon={<SearchIcon />} title="No results" />
        ) : (
          <AssetGrid assets={search.data!.items} />
        )}
      </div>
    </div>
  );
}
